/**
 * This file is the default implementation of the web integration with the purchase flow and a host page.
 * Once the purchase flow is initialized, it provides an API through the window object that the app can use
 * to init the flow.
 */
import root from 'window-or-global';
import {lowercaseObjectProperties} from '../src/utils/lowercaseObjectProperties';
import {
    getBaseUrl,
    getParameterByName,
    getParameterListByName,
    appendLocationHashToUrl,
} from '../src/utils/urlUtils';
import {
    getWebPurchaseIframe,
    loadQuickPurchase,
    loadWebPurchaseContainer,
    closePurchaseContainer,
    getWebPurchaseBackground,
    getWebPurchaseLoading,
    getWebPurchaseCloseBtn,
    getWebPurchaseContainer,
    setCustomPurchaseBgId,
    setCustomPurchaseContainerId,
} from '../src/views/webPurchaseContainer';
import qs from 'query-string';

const WEB_MOCK_BY_NAME = 'epic-launcher-integration / WebPurchaseIntegration.js';

let resolvePromiseSuccess, resolvePromiseFailure, purchaseReceipt;
let paymentWebBase = '';
const purchaseUrlPattern = /^#\/(purchase|subscription|subscribe)\/.*$/;

/**
 * Used by WebMarketplace.RequestPurchase to build the purchase iframe url
 * Side effect: updates [window.history] if a hash is found in [window.location]
 * @param args
 * @param {string} args[].OfferNamespace
 * @param {string} args[].OfferId
 * @param {number} args[].Quantity
 * @param {string} args[].HighlightColor
 * @param {string} [args[].locale]
 * @param {string} [args[].priceToCheck]
 * @param {boolean} [args[].showNavigation=true]
 * @param {object} [options={}]
 * @param {boolean} [options.purchaseV2=false]
 * @returns {string}
 */
export const buildPurchaseUrl = (args, options = {}) => {
    const first = args[0] || {};
    const queryParams = {
        showNavigation: first.showNavigation ? first.showNavigation : true,
        highlightColor: first.HighlightColor ? first.HighlightColor : undefined,
        locale: first.locale ? first.locale : undefined,
    };
    queryParams.orderId = getParameterByName('orderId');
    queryParams.purchaseToken = getParameterByName('purchaseToken');
    if (!options.purchaseV2) {
        queryParams.namespace = first.OfferNamespace;
    }

    if (Array.isArray(args) && args.length) {
        queryParams.offers = [];
        queryParams.offers = args.map((item) => {
            if (options.purchaseV2) {
                return `${item.Quantity}-${item.OfferNamespace}-${item.OfferId}`;
            }
            return item.OfferId;
        });
    }
    const query = qs.stringify(queryParams, {
        skipNull: true,
        skipEmptyString: true,
    });
    let url = `${getBaseUrl()}purchase?${query}`;
    url = appendLocationHashToUrl(url);
    return url;
};

const WebIntegrationAPI = {};
WebIntegrationAPI.WebMarketplace = {
    RequestPurchase(args) {
        // Setup the promises for the purchase flow
        resolvePromiseFailure = null;
        resolvePromiseSuccess = null;
        purchaseReceipt = null;

        return new Promise((resolve, reject) => {
            //Get a handle on the resolve/reject
            let headerOfferPrice;
            resolvePromiseSuccess = resolve;
            resolvePromiseFailure = reject;

            const webPurchaseContainer = loadWebPurchaseContainer();
            if (!webPurchaseContainer) {
                console.error('Unable to find purchase container');
                return;
            }
            //TODO: this block of code does not seem to be used
            if (Array.isArray(args)) {
                args.forEach((item) => {
                    if (item.OfferId && _isNumeric(item.priceToCheck)) {
                        headerOfferPrice = headerOfferPrice
                            ? `${headerOfferPrice}|${item.OfferId}-${item.priceToCheck}`
                            : `${item.OfferId}-${item.priceToCheck}`;
                    }
                });
            }

            //Display quick purchase loading
            if (root.isQuickPurchase) {
                loadQuickPurchase();
            }

            // This approach may not allow us to react to varying response conditions and it is
            // possible that we'll need to resume fetching the document and inserting it in to
            // the iframe manually.
            _bindEvents();
            const iframeEle = getWebPurchaseIframe();
            iframeEle.src = buildPurchaseUrl(args);
        });
    },

    /**
     * Creates a the checkout iframe modal and loads the checkout page from the V2 endpoint.
     * NOTE: This method is mapped to the window object in the configureWindowForWeb method
     * @param args
     * @param {string} args[].OfferNamespace
     * @param {string} args[].OfferId
     * @param {number} args[].Quantity
     * @param {string} args[].HighlightColor
     * @param {string} [args[].locale]
     * @param {boolean} [args[].showNavigation = true]
     *
     * @returns {Promise<Receipt>}
     */
    RequestPurchaseV2(args) {
        // Setup the promises for the purchase flow
        resolvePromiseFailure = null;
        resolvePromiseSuccess = null;
        purchaseReceipt = null;

        return new Promise((resolve, reject) => {
            //Get a handle on the resolve/reject
            resolvePromiseSuccess = resolve;
            resolvePromiseFailure = reject;

            const webPurchaseContainer = loadWebPurchaseContainer();
            if (!webPurchaseContainer) {
                console.error('Unable to find purchase container');
                return;
            }
            //Display quick purchase loading
            if (root.isQuickPurchase) {
                loadQuickPurchase();
            }
            // This approach may not allow us to react to varying response conditions and it is
            // possible that we'll need to resume fetching the document and inserting it in to
            // the iframe manually.
            _bindEvents();
            const iframeEle = getWebPurchaseIframe(webPurchaseContainer);
            iframeEle.src = buildPurchaseUrl(args, {purchaseV2: true});
        });
    },
    //the old getAccountStatus sync request freeze the page and made a delay after user click the buy button,
    //so bypass the login check here. If user's session expired, RequestPurchase callback will handle it correctly.
    isLoggedIn() {
        return true;
    },

    IsWebFlow: true,
};

WebIntegrationAPI.PurchaseFlow = {
    RequestClose(value) {
        const isIFrame = root.parent !== root;
        if (isIFrame) {
            console.log(`WebIntegrationAPI.PurchaseFlow - Request Parent Frame Close: ${value}`);
            root.parent.postMessage(
                {
                    message: {
                        name: 'epicPurchase.requestClose',
                        reason: value,
                    },
                },
                '*'
            );
            return;
        }
        const resolve = resolvePromiseSuccess || root.resolvePromiseSuccess;
        const reject = resolvePromiseFailure || root.resolvePromiseFailure;
        if (!resolve || !reject) {
            console.warn('WebIntegrationAPI.PurchaseFlow.RequestClose has no resolve & reject');
            closePurchaseContainer(value);
            return;
        }
        switch (value) {
            case 'WasSuccessful':
                resolve(purchaseReceipt);
                break;
            case 'UserCanceled':
                reject('User Cancelled Purchase');
                break;
            case 'LoadFailure':
                reject('Purchase page failed to load');
                break;
            default:
                reject('Unknown Close Command');
                break;
        }

        resolvePromiseFailure = root.resolvePromiseFailure = null;
        resolvePromiseSuccess = root.resolvePromiseSuccess = null;
        purchaseReceipt = null;

        // This needs to be the last thing done since this script will stop executing
        // when the iframe containing this script is removed from the DOM
        closePurchaseContainer(value);
    },

    // TODO headerOfferPrice?
    RequestDirectPurchase(params) {
        // Setup the promises for the purchase flow
        resolvePromiseFailure = null;
        resolvePromiseSuccess = null;
        purchaseReceipt = null;

        return new Promise((resolve, reject) => {
            //Get a handle on the resolve/reject
            resolvePromiseSuccess = resolve;
            resolvePromiseFailure = reject;
            const webPurchaseContainer = loadWebPurchaseContainer();

            const {highlightColor = '', orderId = '', purchaseToken} = params;

            if (!webPurchaseContainer) {
                console.error('Unable to find purchase container');
                return;
            }

            if (!purchaseToken) {
                console.error('Unable to find purchaseToken');
                return;
            }

            if (!paymentWebBase) {
                console.error('Unable to find paymentWebBase');
                return;
            }

            const queryStr = qs.stringify({
                purchaseToken,
                showNavigation: true,
                highlightColor,
                orderId,
            });

            let url = `${paymentWebBase}/payment/v1/purchase?${queryStr}`;
            url = appendLocationHashToUrl(url);

            //Display quick purchase loading
            if (root.isQuickPurchase) {
                loadQuickPurchase();
            }

            const iframeEle = getWebPurchaseIframe();
            iframeEle.src = url;
            // This approach may not allow us to react to varying response conditions and it is
            // possible that we'll need to resume fetching the document and inserting it in to
            // the iframe manually.
            _bindEvents();
        });
    },

    Receipt(receipt) {
        const isIFrame = root.parent !== root;
        if (isIFrame) {
            console.log(`WebIntegrationAPI.Receipt - Request Parent Frame Receipt`);
            root.parent.postMessage(
                {
                    message: {
                        name: 'epicPurchase.sendReceipt',
                        receipt,
                    },
                },
                '*'
            );
            return Promise.resolve(receipt);
        }
        purchaseReceipt = receipt;
        return Promise.resolve(receipt);
    },

    IsWebFlow: true,
};

export const initWebIntegration = function initWebIntegration(
    initConfig,
    initOffer,
    onClose,
    consistencyCheckParams,
    onFailure
) {
    console.log('Launcher Integration - Checking for purchase in progress');
    if (initConfig && initConfig.purchaseBg) {
        setCustomPurchaseBgId(initConfig.purchaseBg);
    }
    if (initConfig && initConfig.purchaseContainer) {
        setCustomPurchaseContainerId(initConfig.purchaseContainer);
    }

    let requestParam = [];
    if (initOffer) {
        requestParam = [initOffer];
    } else {
        const offerIds = getParameterListByName('offers');
        const namespace = getParameterByName('namespace');
        const highlightColor = getParameterByName('highlightColor');
        if (offerIds && namespace) {
            offerIds.forEach((value, index) => {
                requestParam.push({
                    OfferNamespace: namespace,
                    OfferId: value,
                    Quantity: 1,
                    HighlightColor: highlightColor,
                });
                if (consistencyCheckParams) {
                    consistencyCheckParams.forEach((item) => {
                        if (item.offerId === value) {
                            requestParam[index].priceToCheck = item.discountPrice
                                ? item.discountPrice
                                : item.price;
                        }
                    });
                }
            });
        }
    }
    const hashLocation = root.location.hash;
    const purchaseToken = getParameterByName('purchaseToken'); //For multiple offers we'll need purchaseToken to get offer info
    const hasOfferInfo = purchaseToken || requestParam.length;
    if (!hashLocation || !purchaseUrlPattern.test(hashLocation) || !hasOfferInfo) {
        return null;
    }
    console.log('Purchase in progress detected, opening purchase flow');

    return root.ue.webmarketplace
        .requestpurchase(requestParam)
        .then(onClose)
        .catch((reason) => {
            console.log('Purchase Failed (web)', reason);
            if (onFailure) {
                onFailure();
            }
        });
};

export const initDirectPurchaseIntegration = function initDirectPurchaseIntegration(
    initConfig,
    onClose,
    onFailure
) {
    console.log('Launcher Integration - Checking for purchase in progress');
    if (initConfig && initConfig.purchaseBg) {
        setCustomPurchaseBgId(initConfig.purchaseBg);
    }
    if (initConfig && initConfig.purchaseContainer) {
        setCustomPurchaseContainerId(initConfig.purchaseContainer);
    }
    if (initConfig && !initConfig.paymentWebBase) {
        console.warn('paymentWebBase is required');
        return;
    }
    paymentWebBase = initConfig.paymentWebBase;

    const hashLocation = root.location.hash;
    if (!hashLocation || !purchaseUrlPattern.test(hashLocation)) return null;
    console.log('Purchase in progress detected, opening purchase flow');

    const purchaseToken = getParameterByName('purchaseToken');

    if (!purchaseToken) return null;

    const highlightColor = getParameterByName('highlightColor');
    const orderId = getParameterByName('orderId');

    const requestParam = {
        purchaseToken,
        highlightColor,
        orderId,
    };

    return root.ue.purchaseflow
        .requestdirectpurchase(requestParam)
        .then(onClose)
        .catch((reason) => {
            console.log('Purchase Failed (web)', reason);
            if (onFailure) {
                onFailure();
            }
        });
};

const paypalUrlRegex = /^(https:\/\/www\.sandbox\.paypal\.com\/|https:\/\/www\.paypal\.com\/)/;
const checkPaypalUrl = (eventObject) => {
    return !!(eventObject && paypalUrlRegex.test(eventObject.paypalUrl));
};

const paymentWebUrlRegex =
    /^https:\/\/(www\.)?payment-((website-(ci|stage|prod|pci|lt))|gamedev)\.ol\.epicgames\.(com|net)\//;
const checkPaymentWebUrl = (url) => {
    return paymentWebUrlRegex.test(url);
};

const purchaseMessageEventListener = function (event) {
    if (event) {
        let eventObject = event.data;
        if (typeof eventObject === 'string') {
            try {
                eventObject = JSON.parse(eventObject);
            } catch (error) {
                // Ignore it is handled in the below check
                console.warn('Could not parse event message');
            }
        }

        if (eventObject) {
            const messageObject = eventObject.message || {};
            switch (messageObject.name) {
                case 'epicPurchase.requestClose': {
                    WebIntegrationAPI.PurchaseFlow.RequestClose(messageObject.reason);
                    break;
                }
                case 'epicPurchase.sendReceipt': {
                    WebIntegrationAPI.PurchaseFlow.Receipt(messageObject.receipt);
                    break;
                }
                case 'epicPurchase.paypalExpressCheckout': {
                    if (checkPaypalUrl(messageObject)) {
                        root.location.href = messageObject.paypalUrl;
                    }
                    break;
                }
                case 'epicPurchase.htmlLoad': {
                    const webPurchaseContainer = getWebPurchaseContainer();
                    webPurchaseContainer.removeChild(getWebPurchaseCloseBtn(webPurchaseContainer));
                    webPurchaseContainer.removeChild(getWebPurchaseLoading(webPurchaseContainer));
                    getWebPurchaseIframe(webPurchaseContainer).setAttribute('class', '');
                    getWebPurchaseBackground().classList.remove('loading');
                    break;
                }
                case 'epicPurchase.adyenRedirectFormSubmit': {
                    const formObject = messageObject.data;
                    if (formObject && formObject.action && checkPaymentWebUrl(formObject.action)) {
                        const form = root.document.createElement('form');
                        form.method = formObject.method;
                        form.action = formObject.action;
                        root.document.body.appendChild(form);
                        if (
                            formObject.body &&
                            Array.isArray(formObject.body) &&
                            formObject.body.length
                        ) {
                            formObject.body.forEach((item) => {
                                const input = root.document.createElement('input');
                                input.type = 'hidden';
                                input.name = item.key;
                                input.value = item.value;
                                form.appendChild(input);
                            });
                        }
                        form.submit();
                    }
                    break;
                }
                default:
                // not epic's event, bypass
            }
        }
    }
};

export const configureWindowForWeb = function mockWindowObjects() {
    //Do not override if root.ue already exists;
    root.ue = root.ue || {
        __mocked: true,
        __mockedBy: WEB_MOCK_BY_NAME,
        PurchaseFlow: WebIntegrationAPI.PurchaseFlow,
        WebMarketplace: WebIntegrationAPI.WebMarketplace,
    };
    if (root.ue.__mocked) {
        console.log('Launcher Integration configured for Web');
    } else {
        console.log('Launcher Integration configured for Launcher');
    }
    root.ue.initWebIntegration = initWebIntegration;

    /**
     * This is a hack that is required by the engine due to the way that they dynamically bind functions to our root.
     * They are unable to guarantee the casing on their functions and thus require that we lowercase everything on our side.
     * It is important that this is run before any other code is run that interfaces with the launcher.
     */
    console.log('Lower-casing object properties on root.ue');
    lowercaseObjectProperties(root.ue);

    // if this is not in an iframe, means this is top level, listen for purchase messages from iframe
    if (root.parent === root) {
        root.removeEventListener('message', purchaseMessageEventListener);
        root.addEventListener('message', purchaseMessageEventListener, false);
    }
};

export const configureWindowForWebV2 = () => {
    if (!root.ue || root.ue.__mockedBy !== WEB_MOCK_BY_NAME) {
        // something is up, maybe the launcher added it's own mock?
        return; // don't override
    }
    // override the purchase method
    root.ue.WebMarketplace = Object.assign(WebIntegrationAPI.WebMarketplace, {
        RequestPurchase: WebIntegrationAPI.WebMarketplace.RequestPurchaseV2,
    });
    // update lowercase version
    lowercaseObjectProperties(root.ue);
};

function _bindEvents() {
    const webPurchaseContainer = getWebPurchaseContainer();
    const iframeElement = getWebPurchaseIframe(webPurchaseContainer);
    iframeElement.removeEventListener('load', _onIFrameLoad);
    iframeElement.addEventListener('load', _onIFrameLoad);

    const closeBtn = getWebPurchaseCloseBtn(webPurchaseContainer);
    closeBtn.removeEventListener('click', _onCloseClick);
    closeBtn.addEventListener('click', _onCloseClick);
}

function _onCloseClick(e) {
    WebIntegrationAPI.PurchaseFlow.RequestClose('UserCanceled');
}

function _onIFrameLoad(e) {
    const webPurchaseContainer = getWebPurchaseContainer();
    const iframeEle = getWebPurchaseIframe(webPurchaseContainer);

    try {
        // this related to purchaseMessageEventListener, epicPurchase.htmlLoad window event
        // Test for successful loading of the remote document. When the embedded source success load, the loading spin will be removed
        // but if errors happen, the loading spin will persist, then close it.
        if (!iframeEle || getWebPurchaseLoading(webPurchaseContainer)) {
            console.error('Unable to confirm load of purchase flow');
            WebIntegrationAPI.PurchaseFlow.RequestClose('LoadFailure');
        } else {
            console.log('Purchase flow loaded successfully');
            try {
                iframeEle.contentWindow.resolvePromiseSuccess = resolvePromiseSuccess;
                iframeEle.contentWindow.resolvePromiseFailure = resolvePromiseFailure;
            } catch (e) {
                console.log(e);
            }
        }
    } catch (e) {
        console.error(`[Catch] Failed to load purchase flow: ${e}`);
        console.dir(e);
        WebIntegrationAPI.PurchaseFlow.RequestClose('LoadFailure');
    }
}

function _isNumeric(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}

if (!Array.isArray) {
    Array.isArray = (arg) => {
        return Object.prototype.toString.call(arg) === '[object Array]';
    };
}

/**
 * Since purchaseMessageEventListener is private, there may be cases where the consuming application
 * may want to handle these events on its own.
 */
export const removePurchaseMessageEventListener = () => {
    root.removeEventListener('message', purchaseMessageEventListener);
};

/**
 * Used to test internal methods in jest
 */
export const internal = () => {
    if (process.env.NODE_ENV === 'test') {
        return {
            WebIntegrationAPI,
            checkPaypalUrl,
            checkPaymentWebUrl,
            purchaseMessageEventListener,
            _bindEvents,
            _onCloseClick,
            _onIFrameLoad,
            _isNumeric,
            setResolvePromiseSuccess: (x) => {
                resolvePromiseSuccess = x;
            },
            setResolvePromiseFailure: (x) => {
                resolvePromiseFailure = x;
            },
            setPurchaseReceipt: (x) => {
                purchaseReceipt = x;
            },
            setPaymentWebBase: (x) => {
                paymentWebBase = x;
            },
        };
    }
    return {};
};
